import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@formrails/environments';
import {
  UpsertWorkflowDto,
  UpsertWorkflowNodeDto,
  WorkflowResponseDto,
  WorkflowNodeResponseDto
} from '../models';

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * HTTP access for workflow definitions and their nodes. Targets the
 * pmp-workflow-api `WorkflowController` and `WorkflowNodeController`
 * (both standard CRUD surfaces). Auth0 HTTP interceptor adds the bearer
 * token.
 *
 * Wire normalisation:
 * - The API serialises enums as PascalCase names ("SendEmail"); the UI
 *   enums are camelCase ("sendEmail"). Responses are normalised here.
 *   Requests go out camelCase — System.Text.Json enum parsing is
 *   case-insensitive.
 * - Unset successors are the empty GUID on the wire, '' in the UI.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.workflowApi}/Workflow`;
  private readonly nodeBase = `${environment.workflowApi}/WorkflowNode`;

  // --- Workflow CRUD ---

  /** GET /Workflow */
  listWorkflows(): Observable<WorkflowResponseDto[]> {
    return this.http.get<WorkflowResponseDto[]>(this.base);
  }

  /** GET /Workflow/{id} */
  getWorkflow(workflowId: string): Observable<WorkflowResponseDto> {
    return this.http.get<WorkflowResponseDto>(`${this.base}/${workflowId}`);
  }

  /** POST /Workflow */
  createWorkflow(request: UpsertWorkflowDto): Observable<WorkflowResponseDto> {
    return this.http.post<WorkflowResponseDto>(this.base, request);
  }

  /** PUT /Workflow/{id} */
  updateWorkflow(workflowId: string, request: UpsertWorkflowDto): Observable<WorkflowResponseDto> {
    return this.http.put<WorkflowResponseDto>(`${this.base}/${workflowId}`, request);
  }

  /** DELETE /Workflow/{id} */
  deleteWorkflow(workflowId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${workflowId}`);
  }

  // --- Workflow node CRUD ---

  /** GET /Workflow/{id}/nodes */
  getWorkflowNodes(workflowId: string): Observable<WorkflowNodeResponseDto[]> {
    return this.http
      .get<WorkflowNodeResponseDto[]>(`${this.base}/${workflowId}/nodes`)
      .pipe(map(nodes => nodes.map(fromWireNode)));
  }

  /** GET /WorkflowNode/{id} */
  getWorkflowNode(nodeId: string): Observable<WorkflowNodeResponseDto> {
    return this.http
      .get<WorkflowNodeResponseDto>(`${this.nodeBase}/${nodeId}`)
      .pipe(map(fromWireNode));
  }

  /** POST /WorkflowNode — the server assigns the id (returned on the DTO). */
  createWorkflowNode(request: UpsertWorkflowNodeDto): Observable<WorkflowNodeResponseDto> {
    return this.http
      .post<WorkflowNodeResponseDto>(this.nodeBase, toWireNode(request))
      .pipe(map(fromWireNode));
  }

  /** PUT /WorkflowNode/{id} */
  updateWorkflowNode(
    nodeId: string,
    request: UpsertWorkflowNodeDto
  ): Observable<WorkflowNodeResponseDto> {
    return this.http
      .put<WorkflowNodeResponseDto>(`${this.nodeBase}/${nodeId}`, toWireNode(request))
      .pipe(map(fromWireNode));
  }

  /** DELETE /WorkflowNode/{id} */
  deleteWorkflowNode(nodeId: string): Observable<void> {
    return this.http.delete<void>(`${this.nodeBase}/${nodeId}`);
  }
}

// --- Wire mapping helpers ---

/** Lowercase the first character — "SendEmail" → "sendEmail". */
function camel<T extends string | undefined | null>(value: T): T {
  if (!value) return value;
  return (value.charAt(0).toLowerCase() + value.slice(1)) as T;
}

/** Normalise a node coming off the wire into the UI's shape. */
function fromWireNode(dto: WorkflowNodeResponseDto): WorkflowNodeResponseDto {
  return {
    ...dto,
    type: camel(dto.type as string) as WorkflowNodeResponseDto['type'],
    ruleType: camel(dto.ruleType as string | undefined) as WorkflowNodeResponseDto['ruleType'],
    actionType: camel(dto.actionType as string | undefined) as WorkflowNodeResponseDto['actionType'],
    requiredMetadata: dto.requiredMetadata ?? [],
    definition: {
      ...dto.definition,
      responseType: camel(dto.definition?.responseType as string | undefined) as never
    },
    successorTrue: dto.successorTrue === EMPTY_GUID ? '' : dto.successorTrue,
    successorFalse:
      !dto.successorFalse || dto.successorFalse === EMPTY_GUID ? undefined : dto.successorFalse
  };
}

/** Prepare a node for the wire: unset successors become the empty GUID. */
function toWireNode(dto: UpsertWorkflowNodeDto): UpsertWorkflowNodeDto {
  return {
    ...dto,
    successorTrue: dto.successorTrue || EMPTY_GUID,
    successorFalse: dto.successorFalse || null
  };
}
